/**
 * @copyright 2025 @ DigiNet
 * @author ANHTAI
 * @create 11/02/2025
 * @Example
 */

import { HelperText, Label } from 'diginet-core-ui/components';
import PropTypes from "prop-types";
import React, { forwardRef, memo, useImperativeHandle, useRef, useState } from 'react';
import NumberFormat from 'react-number-format';
import Config from '../../../config';
import { Input } from "reactstrap";

const typeTimes = ['hh:mm', 'hh:mm:ss'];

const TimeFormatField = React.memo(forwardRef((props, ref) => {
    const refText = useRef(null);
    const refNumberF = useRef(null);
    const refWarning = useRef(null);
    const { disabled, value, placeholder, error, className, label, type, onChange, onError, required } = props;

    useImperativeHandle(ref, () => ({
        timeFormatInput,
        isValidTime
    }));

    const isValidTime = (val = '', isAllowEmpty = false) => {
        if (isAllowEmpty && Config.isEmpty(val)) return true;
        if (Config.isEmpty(val) || Config.isEmpty(type)) return false;
        return type.replaceAll(':', '').length === val.replaceAll(':', '').length;
    };

    const handleChange = (e) => {
        let val = e?.value ?? e?.target?.value ?? '';
        if (val.includes(':') && typeof val === 'string') val = val.replaceAll(":", "");
        onChange && onChange(val);
    };

    const limitTimeFormat = (val, max) => {
        if (val.length === 1 && val[0] > max[0]) {
            val = '0' + val;
        }
        if (val.length === 2 && val > max) {
            val = max;
        }
        return val;
    };

    const timeFormatInput = (val) => {
        let result = String(val);
        if (result.indexOf(':') > -1) return result;
        let hour = limitTimeFormat(result.substring(0, 2), "23");
        let minute = limitTimeFormat(result.substring(2, 4), "59");
        let second = limitTimeFormat(result.substring(4, 6), "59");
        result = hour + (minute.length ? `:${minute}` : '');
        if (type === "hh:mm:ss") result = result + (second.length ? `:${second}` : '');
        return result;
    };

    return (
        <div style={{ position: 'relative' }}>
            {label && <Label required={required} disabled={disabled}>{label}</Label>}
            <NumberFormat
                ref={refNumberF}
                inputRef={refText}
                className={`${className}`}
                value={value}
                disabled={disabled}
                onKeyUp={(e) => {
                    const val = e?.target?.value.replaceAll(':', '');
                    if (refWarning.current && e) {
                        const typeLength = type.replaceAll(':', '').length;
                        if (val.length === 0 || val.length === typeLength) {
                            refWarning.current.setWarning('');
                        } else if (val.length < typeLength) {
                            const warningText = Config.lang('Gio_khong_dung_dinh_dang');
                            !!error && onError && onError(warningText);
                            refWarning.current.setWarning(warningText);
                        }
                    }
                }}
                customInput={Input}
                placeholder={placeholder || (typeTimes.includes(type) ? type : '')}
                format={timeFormatInput}
                onChange={e => onChange && handleChange(e)}
            />
            {!disabled && <Warning ref={ref => refWarning.current = ref} error={error} />}
        </div>
    )
}));

const Warning = memo(forwardRef((props, ref) => {
    const [warning, setWarning] = useState("");
    const { error } = props;
    useImperativeHandle(ref, () => ({ setWarning, warning }));
    return (warning || error) && <HelperText hoverTooltip children={warning || error} />
}));

TimeFormatField.defaultProps = {
    label: "",
    startIcon: "",
    className: "",
    endIcon: "Clock",
    type: "hh:mm"
};

TimeFormatField.propTypes = {
    value: PropTypes.any,
    disabled: PropTypes.bool,
    required: PropTypes.bool,
    error: PropTypes.string,
    type: PropTypes.string,
    label: PropTypes.string,
    placeholder: PropTypes.string,
    defaultValue: PropTypes.any,
    onChange: PropTypes.func,
    onError: PropTypes.func,
};

export default TimeFormatField;